import React, { useState, useRef } from "react";
import "./styles.sass";
import { useClassroomQuizSummaryHandler } from "./handler";
import { LatexRenderer } from "../../components/latex-renderer";

const formatQuestionType = (type) => {
  if (!type || typeof type !== "string") return "MCQ";
  const upper = type.toUpperCase().replace(/\s+/g, "_");
  const dictionary = {
    MCQ: "MCQ",
    SINGLE_CORRECT: "Single Correct MCQ",
    MULTIPLE_CORRECT: "Multiple Correct MCQ",
    MULTIPLE_CHOICE: "Multiple Correct MCQ",
    ASSERTION_REASON: "Assertion & Reason",
    ASSERTION_REASONING: "Assertion & Reason",
    ASSERTION_AND_REASONING: "Assertion & Reason",
    IMAGE_BASED_ASSERTION_AND_REASONING: "Image-Based Assertion & Reason",
    IMAGE_BASED_ASSERTION_REASON: "Image-Based Assertion & Reason",
    IMAGE_BASED_ASSERTION_REASONING: "Image-Based Assertion & Reason",
    IMAGE_BASED_MCQ: "Image-Based MCQ",
    IMAGE_BASED_STATEMENT: "Image-Based Statement",
    IMAGE_BASED_STATEMENT_TYPE: "Image-Based Statement",
    IMAGE_BASED_STATEMENT_QUESTION: "Image-Based Statement",
    MATCH_THE_FOLLOWING: "Match the Following",
    MATCHING: "Match the Following",
    STATEMENT_TYPE: "Statement Type",
    STATEMENT_QUESTION: "Statement Question",
    STATEMENT: "Statement Question",
    CONCEPTUAL: "Conceptual",
    NUMERICAL: "Numerical",
    NUMERIC: "Numerical",
    INTEGER: "Integer",
    PARAGRAPH: "Paragraph",
    SHORT: "Short Answer",
  };

  if (dictionary[upper]) return dictionary[upper];

  return type
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => {
      const lower = w.toLowerCase();
      if (lower === "mcq") return "MCQ";
      if (lower === "and") return "&";
      if (lower === "the") return "the";
      if (lower === "of") return "of";
      return w.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
};

const handleImageError = (e) => {
  const img = e.currentTarget;
  const currentSrc = img.src || "";
  if (!img.dataset.retried) {
    img.dataset.retried = "1";
    if (currentSrc.includes("/secure-api/")) {
      // Fallback: try direct port 8001
      img.src = currentSrc
        .replace("https://", "http://")
        .replace("/secure-api/", "/")
        .replace("visal1chatbotvm.southindia.cloudapp.azure.com", "visal1chatbotvm.southindia.cloudapp.azure.com:8001");
      return;
    } else if (currentSrc.includes(":8001")) {
      // Fallback: try https /secure-api/
      img.src = currentSrc
        .replace("http://", "https://")
        .replace(":8001/", "/secure-api/")
        .replace("visal1chatbotvm.southindia.cloudapp.azure.com:8001", "visal1chatbotvm.southindia.cloudapp.azure.com");
      return;
    }
  }
  img.style.display = "none";
};

export const ClassroomQuizSummary = () => {
  const {
    activeTab,
    setActiveTab,
    loading,
    summaryMeta,
    kpis,
    scoreDistribution,
    yAxisConfig,
    individualScores,
    notStartedStudents,
    questionsList,
    matrixStudents,
    matrixQuestions,
    handleBackToRoom,
  } = useClassroomQuizSummaryHandler();

  const [zoomModalImage, setZoomModalImage] = useState(null);
  const [zoomScale, setZoomScale] = useState(1.5);

  const handleOpenZoom = (e, fallbackSrc, title) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    const finalSrc = fallbackSrc;
    if (!finalSrc) return;
    setZoomScale(1.5);
    setZoomModalImage({ src: finalSrc, title });
  };

  const matrixScrollRef = useRef(null);

  const handleScrollLeft = () => {
    if (matrixScrollRef.current) {
      matrixScrollRef.current.scrollBy({ left: -320, behavior: "smooth" });
    }
  };

  const handleScrollRight = () => {
    if (matrixScrollRef.current) {
      matrixScrollRef.current.scrollBy({ left: 320, behavior: "smooth" });
    }
  };

  const { yMax, yTicks } = yAxisConfig;

  return (
    <div className="quiz_summary_page">
      {/* Top Header */}
      <header className="summary_header">
        <div className="summary_title_left">
          <div className="back_nav_btn" onClick={handleBackToRoom}>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            <span>Back to Room</span>
          </div>
          <h1>Classroom Quiz Summary</h1>
          <p>Review quiz performance, participation</p>
        </div>

        <div className="summary_meta_right">
          <div className="meta_badge room_code_badge">
            Room Code: <strong>{summaryMeta.roomCode}</strong>
          </div>
          <div className="meta_badge plain_badge">
            Mode: <strong>{summaryMeta.mode}</strong>
          </div>
          <div className="meta_badge plain_badge">
            Course Type: <strong>{summaryMeta.courseType}</strong>
          </div>
          <div className={`meta_badge status_badge ${summaryMeta.status.toLowerCase()}`}>
            Status: <strong>{summaryMeta.status}</strong>
          </div>
        </div>
      </header>

      {/* 5 KPI Metric Cards */}
      <section className="kpi_cards_container">
        {/* 1. Total Responses */}
        <div className="kpi_card">
          <div className="kpi_icon_wrapper icon_blue">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div className="kpi_info">
            <span className="kpi_label">Total Responses</span>
            <h2 className="kpi_value">{kpis.totalResponses}</h2>
          </div>
        </div>

        {/* 2. Average Score */}
        <div className="kpi_card">
          <div className="kpi_icon_wrapper icon_yellow">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </div>
          <div className="kpi_info">
            <span className="kpi_label">Average Score</span>
            <h2 className="kpi_value">{kpis.averageScore}</h2>
          </div>
        </div>

        {/* 3. Median Score */}
        <div className="kpi_card">
          <div className="kpi_icon_wrapper icon_cyan">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0284C7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="20" x2="18" y2="10" />
              <line x1="12" y1="20" x2="12" y2="4" />
              <line x1="6" y1="20" x2="6" y2="14" />
            </svg>
          </div>
          <div className="kpi_info">
            <span className="kpi_label">Median Score</span>
            <h2 className="kpi_value">{kpis.medianScore}</h2>
          </div>
        </div>

        {/* 4. Min Score */}
        <div className="kpi_card">
          <div className="kpi_icon_wrapper icon_red">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
              <polyline points="17 18 23 18 23 12" />
            </svg>
          </div>
          <div className="kpi_info">
            <span className="kpi_label">Min Score</span>
            <h2 className="kpi_value">{kpis.minScore}</h2>
          </div>
        </div>

        {/* 5. Max Score */}
        <div className="kpi_card">
          <div className="kpi_icon_wrapper icon_green">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
              <polyline points="17 6 23 6 23 12" />
            </svg>
          </div>
          <div className="kpi_info">
            <span className="kpi_label">Max Score</span>
            <h2 className="kpi_value">{kpis.maxScore}</h2>
          </div>
        </div>
      </section>

      {/* Tabs Navigation */}
      <section className="summary_tabs_container">
        <button
          className={`tab_button ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          Dashboard Overview
        </button>
        <button
          className={`tab_button ${activeTab === "questions" ? "active" : ""}`}
          onClick={() => setActiveTab("questions")}
        >
          Question Analysis
        </button>
        <button
          className={`tab_button ${activeTab === "responses" ? "active" : ""}`}
          onClick={() => setActiveTab("responses")}
        >
          Individual Responses
        </button>
      </section>

      {loading && (
        <div className="summary_loading_state">
          <div className="summary_spinner"></div>
          <p>Loading 360° Quiz Analysis...</p>
        </div>
      )}

      {!loading && (
        <div className="tab_content_wrapper">
          {/* TAB 1: DASHBOARD OVERVIEW */}
          {activeTab === "overview" && (
            <div className="tab_pane overview_pane">
              <div className="top_overview_grid">
                {/* Score Distribution Chart */}
                <div className="summary_card score_distribution_card">
                  <h3>Score Distribution</h3>
                  <div className="chart_body">
                    <div className="chart_y_label">Number of Students</div>
                    <div className="chart_inner">
                      <div className="y_axis_ticks">
                        {/* Dynamic ticks from yAxisConfig */}
                        {yTicks.map((tick, i) => (
                          <span key={i}>{tick}</span>
                        ))}
                      </div>
                      <div className="chart_bars_and_labels">
                        <div className="chart_bars_area">
                          {/* Horizontal grid lines — one per tick interval */}
                          <div className="chart_grid_lines">
                            {yTicks.map((_, i) => (
                              <div
                                key={i}
                                className={`grid_line${i === yTicks.length - 1 ? " grid_line_bottom" : ""}`}
                              ></div>
                            ))}
                          </div>
                          {scoreDistribution.map((item, idx) => {
                            const heightPercent = yMax > 0 ? (item.count / yMax) * 100 : 0;
                            return (
                              <div className="chart_column" key={idx}>
                                <div className="bar_container">
                                  {item.count > 0 && (
                                    <span className="bar_count_label">{item.count}</span>
                                  )}
                                  <div
                                    className={`chart_bar range_${idx}`}
                                    style={{
                                      height: `${Math.max(heightPercent, item.count > 0 ? 4 : 0)}%`,
                                    }}
                                  ></div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {/* X-axis labels — rendered outside the bordered area */}
                        <div className="x_labels_row">
                          {scoreDistribution.map((item, idx) => (
                            <span key={idx} className="x_axis_label">{item.range}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="chart_x_label">Score Range</div>
                  </div>
                </div>

                {/* Score Statistics Card */}
                <div className="summary_card score_statistics_card">
                  <div className="card_header_with_icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="4" y1="21" x2="4" y2="14" />
                      <line x1="4" y1="10" x2="4" y2="3" />
                      <line x1="12" y1="21" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12" y2="3" />
                      <line x1="20" y1="21" x2="20" y2="16" />
                      <line x1="20" y1="12" x2="20" y2="3" />
                      <line x1="1" y1="14" x2="7" y2="14" />
                      <line x1="9" y1="8" x2="15" y2="8" />
                      <line x1="17" y1="16" x2="23" y2="16" />
                    </svg>
                    <h3>Score Statistics</h3>
                  </div>

                  <div className="stats_list">
                    <div className="stat_row">
                      <span className="stat_name">Average Score</span>
                      <span className="stat_val">
                        {typeof kpis.averageScore === "string" && kpis.averageScore.includes("/") ? (
                          <>
                            <span className="score_num">{kpis.averageScore.split("/")[0].trim()}</span>
                            <span className="score_denom"> / {kpis.averageScore.split("/")[1].trim()}</span>
                          </>
                        ) : (
                          kpis.averageScore
                        )}
                      </span>
                    </div>
                    <div className="stat_row">
                      <span className="stat_name">Median Score</span>
                      <span className="stat_val">
                        {typeof kpis.medianScore === "string" && kpis.medianScore.includes("/") ? (
                          <>
                            <span className="score_num">{kpis.medianScore.split("/")[0].trim()}</span>
                            <span className="score_denom"> / {kpis.medianScore.split("/")[1].trim()}</span>
                          </>
                        ) : (
                          kpis.medianScore
                        )}
                      </span>
                    </div>
                    <div className="stat_row">
                      <span className="stat_name">Min Score</span>
                      <span className="stat_val">
                        {typeof kpis.minScore === "string" && kpis.minScore.includes("/") ? (
                          <>
                            <span className="score_num">{kpis.minScore.split("/")[0].trim()}</span>
                            <span className="score_denom"> / {kpis.minScore.split("/")[1].trim()}</span>
                          </>
                        ) : (
                          kpis.minScore
                        )}
                      </span>
                    </div>
                    <div className="stat_row">
                      <span className="stat_name">Max Score</span>
                      <span className="stat_val">
                        {typeof kpis.maxScore === "string" && kpis.maxScore.includes("/") ? (
                          <>
                            <span className="score_num">{kpis.maxScore.split("/")[0].trim()}</span>
                            <span className="score_denom"> / {kpis.maxScore.split("/")[1].trim()}</span>
                          </>
                        ) : (
                          kpis.maxScore
                        )}
                      </span>
                    </div>
                    <div className="stat_row">
                      <span className="stat_name">Total Responses</span>
                      <span className="stat_val">{kpis.totalResponses}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Individual Score Summary Table */}
              <div className="summary_card individual_summary_card">
                <div className="card_header_with_icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#612ef7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
                    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
                    <path d="M4 22h16" />
                    <path d="M10 14.66V17c0 .55-.45 1-1 1H7.5a1.5 1.5 0 0 1 0-3H8" />
                    <path d="M14 14.66V17c0 .55.45 1 1 1h1.5a1.5 1.5 0 0 0 0-3H16" />
                    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
                  </svg>
                  <h3>Individual Score Summary</h3>
                </div>

                <div className="table_responsive">
                  <table className="summary_table individual_table">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Nickname</th>
                        <th>Email</th>
                        <th>Score</th>
                        <th>Correct</th>
                        <th>Total</th>
                        <th>Percentage %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {individualScores.length > 0 ? (
                        individualScores.map((row, idx) => {
                          // Parse percentage number for color logic
                          const pctNum = parseFloat(String(row.percentage).replace("%", ""));
                          const pctClass = pctNum >= 60 ? "pct_green" : "pct_orange";

                          // Split score into number and denominator
                          const scoreParts = String(row.score).split("/");
                          const scoreNum = scoreParts[0]?.trim();
                          const scoreDenom = scoreParts[1]?.trim();

                          return (
                            <tr key={idx}>
                              <td className="rank_cell">{row.rank}</td>
                              <td className="nickname_cell nickname_bold">{row.nickname}</td>
                              <td className="email_cell">{row.email}</td>
                              <td className="score_split_cell">
                                <span className="score_num">{scoreNum}</span>
                                {scoreDenom && <span className="score_denom"> / {scoreDenom}</span>}
                              </td>
                              <td className="plain_cell">{row.correct}</td>
                              <td className="plain_cell">{row.total}</td>
                              <td className={`percent_cell ${pctClass}`}>{row.percentage}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="7" className="empty_cell">No submissions found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Not Started Students Table */}
              <div className="summary_card not_started_card">
                <div className="card_header_with_icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  <h3>Not Started</h3>
                </div>

                <div className="table_responsive">
                  <table className="summary_table not_started_table">
                    <thead>
                      <tr>
                        <th>Nickname</th>
                        <th>Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notStartedStudents.length > 0 ? (
                        notStartedStudents.map((row, idx) => (
                          <tr key={idx}>
                            <td className="nickname_cell">{row.nickname}</td>
                            <td className="email_cell">{row.email}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="2" className="empty_cell">All students have started the test</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: QUESTION ANALYSIS */}
          {activeTab === "questions" && (
            <div className="tab_pane questions_pane">
              <div className="qa_unified_card">
                {/* TABLE HEADER */}
                <div className="qa_table_header">
                  <div className="qa_col_q_title">Question Details</div>
                  <div className="qa_col_q_type">Type</div>
                  <div className="qa_col_q_responses">Responses</div>
                  <div className="qa_col_q_correct">Correct</div>
                  <div className="qa_col_q_incorrect">Incorrect</div>
                  <div className="qa_col_q_skipped">Skipped</div>
                  <div className="qa_col_q_pct">Accuracy</div>
                </div>

                {/* QUESTIONS LIST */}
                <div className="qa_questions_list">
                  {questionsList.length > 0 ? (
                    questionsList.map((q) => {
                      const totalStudents = Math.max(
                        (q.correctCount || 0) +
                          (q.incorrectCount || 0) +
                          (q.skippedCount || 0),
                        q.totalResponses || 0,
                        1
                      );
                      const correctPct = Math.round(
                        ((q.correctCount || 0) / totalStudents) * 100
                      );
                      const incorrectPct = Math.round(
                        ((q.incorrectCount || 0) / totalStudents) * 100
                      );
                      const skippedPct = (q.skippedCount || 0) > 0
                        ? Math.round(((q.skippedCount || 0) / totalStudents) * 100)
                        : 0;

                      const optionLetters = ["A", "B", "C", "D", "E", "F"];

                      return (
                        <div className="qa_question_item" key={q.id}>
                          {/* TOP STATS ROW */}
                          <div className="qa_item_top_row">
                            <div className="qa_q_main_info">
                              <div className="qa_q_title">
                                <span className="qa_q_badge">Q{q.questionNumber}</span>
                                {q.questionText ? (
                                  <div className="qa_q_text_content">
                                    <LatexRenderer text={q.questionText} />
                                  </div>
                                ) : null}
                              </div>

                              {/* Question Diagram / Image */}
                              {q.imageUrls && q.imageUrls.length > 0 && (
                                <div className="qa_q_images">
                                  {q.imageUrls.map((imgUrl, imgIdx) => (
                                    <div key={imgIdx} className="qa_image_preview_box">
                                      <img
                                        src={imgUrl}
                                        alt={`Question ${q.questionNumber} diagram ${imgIdx + 1}`}
                                        className="qa_q_image"
                                        onError={handleImageError}
                                        onClick={(e) =>
                                          handleOpenZoom(
                                            e,
                                            imgUrl,
                                            `Question Q${q.questionNumber} Diagram`
                                          )
                                        }
                                      />
                                      <button
                                        type="button"
                                        className="qa_img_zoom_btn"
                                        title="Click to Zoom In"
                                        onClick={(e) =>
                                          handleOpenZoom(
                                            e,
                                            imgUrl,
                                            `Question Q${q.questionNumber} Diagram`
                                          )
                                        }
                                      >
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                          <circle cx="11" cy="11" r="8"></circle>
                                          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                          <line x1="11" y1="8" x2="11" y2="14"></line>
                                          <line x1="8" y1="11" x2="14" y2="11"></line>
                                        </svg>
                                        <span>Zoom In</span>
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Correct Answer Banner */}
                              {q.correctAnswer && (
                                <div className="qa_correct_banner">
                                  <span className="qa_correct_icon">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                  </span>
                                  <span className="qa_correct_label">Correct Answer:</span>
                                  <span className="qa_correct_val">
                                    <LatexRenderer text={q.correctAnswer} inline />
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Summary Metric Columns */}
                            <div className="qa_stat_val qa_col_q_type">
                              <span className="qa_type_pill">{formatQuestionType(q.type)}</span>
                            </div>
                            <div className="qa_stat_val qa_col_q_responses">
                              <span className="qa_stat_pill responses">{q.totalResponses}</span>
                            </div>
                            <div className="qa_stat_val qa_col_q_correct">
                              <span className="qa_stat_pill correct">{q.correctCount}</span>
                            </div>
                            <div className="qa_stat_val qa_col_q_incorrect">
                              <span className="qa_stat_pill incorrect">{q.incorrectCount}</span>
                            </div>
                            <div className="qa_stat_val qa_col_q_skipped">
                              <span className="qa_stat_pill skipped">{q.skippedCount || 0}</span>
                            </div>
                            <div className="qa_stat_val qa_col_q_pct">
                              <div className="qa_stat_pct_block">
                                <span className={`qa_pct_value ${correctPct >= 70 ? "high" : correctPct >= 40 ? "med" : "low"}`}>
                                  {correctPct}%
                                </span>
                                <div className="qa_mini_bar_track">
                                  <div
                                    className="qa_mini_bar_fill"
                                    style={{
                                      width: `${correctPct}%`,
                                      backgroundColor: correctPct >= 70 ? "#10b981" : correctPct >= 40 ? "#f59e0b" : "#ef4444",
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* ACCURACY BAR & LEGEND */}
                          <div className="qa_accuracy_section">
                            <div className="qa_bar_container">
                              {correctPct > 0 && (
                                <div
                                  className="qa_bar_segment qa_bar_green"
                                  style={{ width: `${correctPct}%` }}
                                  title={`Correct: ${q.correctCount} (${correctPct}%)`}
                                >
                                  <span className="qa_bar_label">{correctPct}%</span>
                                </div>
                              )}
                              {incorrectPct > 0 && (
                                <div
                                  className="qa_bar_segment qa_bar_pink"
                                  style={{ width: `${incorrectPct}%` }}
                                  title={`Incorrect: ${q.incorrectCount} (${incorrectPct}%)`}
                                >
                                  <span className="qa_bar_label">{incorrectPct}%</span>
                                </div>
                              )}
                              {skippedPct > 0 && (
                                <div
                                  className="qa_bar_segment qa_bar_gray"
                                  style={{ width: `${skippedPct}%` }}
                                  title={`Skipped: ${q.skippedCount} (${skippedPct}%)`}
                                >
                                  <span className="qa_bar_label">{skippedPct}%</span>
                                </div>
                              )}
                            </div>
                            <div className="qa_bar_legend">
                              <span className="qa_legend_item">
                                <span className="qa_legend_dot correct" />
                                Correct: <strong>{q.correctCount}</strong> ({correctPct}%)
                              </span>
                              <span className="qa_legend_item">
                                <span className="qa_legend_dot incorrect" />
                                Incorrect: <strong>{q.incorrectCount}</strong> ({incorrectPct}%)
                              </span>
                              <span className="qa_legend_item">
                                <span className="qa_legend_dot skipped" />
                                Skipped: <strong>{q.skippedCount || 0}</strong> ({skippedPct}%)
                              </span>
                            </div>
                          </div>

                          {/* STRUCTURED OPTION CARDS GRID */}
                          {q.options && q.options.length > 0 && (
                            <div className="qa_options_grid">
                              {q.options.map((opt, oIdx) => {
                                const letter = optionLetters[oIdx] || String(oIdx + 1);
                                const isOptionCorrect =
                                  opt.is_correct ||
                                  (q.correctAnswer &&
                                    (q.correctAnswer.trim() === opt.text?.trim() ||
                                      q.correctAnswer.trim().toLowerCase() === letter.toLowerCase()));
                                const optPct = totalStudents > 0 ? Math.round(((opt.count || 0) / totalStudents) * 100) : 0;

                                return (
                                  <div
                                    key={oIdx}
                                    className={`qa_opt_card ${isOptionCorrect ? "is_correct" : ""} ${(opt.count || 0) > 0 ? "has_responses" : ""}`}
                                  >
                                    <div className="qa_opt_card_header">
                                      <span className="qa_opt_letter_badge">{letter}</span>
                                      {isOptionCorrect && (
                                        <span className="qa_opt_correct_tag">
                                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5">
                                            <polyline points="20 6 9 17 4 12" />
                                          </svg>
                                          Correct
                                        </span>
                                      )}
                                      <span className="qa_opt_student_count">
                                        <strong>{opt.count || 0}</strong> {opt.count === 1 ? "student" : "students"} ({optPct}%)
                                      </span>
                                    </div>

                                    <div className="qa_opt_card_body">
                                      <div className="qa_opt_card_text">
                                        <LatexRenderer text={opt.text} inline />
                                      </div>
                                      {opt.choiceImage && (
                                        <div className="qa_opt_image_wrapper">
                                          <img
                                            src={opt.choiceImage}
                                            alt={`Option ${letter}`}
                                            className="qa_opt_image"
                                            onError={handleImageError}
                                            onClick={(e) =>
                                              handleOpenZoom(
                                                e,
                                                opt.choiceImage,
                                                `Option ${letter} Image`
                                              )
                                            }
                                          />
                                        </div>
                                      )}
                                    </div>

                                    {/* Subtle bottom percentage fill */}
                                    <div className="qa_opt_progress_bar">
                                      <div
                                        className={`qa_opt_progress_fill ${isOptionCorrect ? "fill_green" : "fill_neutral"}`}
                                        style={{ width: `${optPct}%` }}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="qa_empty_state">
                      No question analysis data available
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: INDIVIDUAL RESPONSES (FLEX-BASED STICKY MATRIX) */}
          {activeTab === "responses" && (
            <div className="tab_pane responses_pane">
              {/* HORIZONTAL SCROLL TOOLBAR & CONTROLS */}
              <div className="matrix_toolbar">
                <div className="matrix_hint_badge">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#64748b"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="15 18 9 12 15 6" />
                    <polyline points="19 18 13 12 19 6" />
                  </svg>
                  <span>
                    Scroll horizontally to view all questions (
                    {matrixQuestions.length} Questions)
                  </span>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#64748b"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="9 18 15 12 9 6" />
                    <polyline points="5 18 11 12 5 6" />
                  </svg>
                </div>
                <div className="matrix_nav_buttons">
                  <button
                    className="matrix_nav_btn"
                    onClick={handleScrollLeft}
                    title="Scroll Left"
                    type="button"
                  >
                    ‹
                  </button>
                  <button
                    className="matrix_nav_btn"
                    onClick={handleScrollRight}
                    title="Scroll Right"
                    type="button"
                  >
                    ›
                  </button>
                </div>
              </div>

              <div className="responses_matrix_card">
                <div className="matrix_scroll_container" ref={matrixScrollRef}>
                  <div className="matrix_grid_table">
                    {/* HEADER ROW */}
                    <div className="matrix_row matrix_header_row">
                      <div className="matrix_cell cell_student col_sticky_1">
                        Student
                      </div>
                      <div className="matrix_cell cell_score col_sticky_2">
                        Score
                      </div>
                      {matrixQuestions.map((mq, idx) => (
                        <div key={idx} className="matrix_cell cell_question">
                          {mq.title || `Q${idx + 1}`}
                        </div>
                      ))}
                    </div>

                    {/* BODY ROWS */}
                    {matrixStudents.length > 0 ? (
                      matrixStudents.map((s, sIdx) => {
                        const rawPctNum =
                          parseFloat(String(s.percentage).replace("%", "")) || 0;
                        const pctColorClass =
                          rawPctNum >= 70
                            ? "pct_green"
                            : rawPctNum >= 40
                            ? "pct_orange"
                            : "pct_red";

                        return (
                          <div key={sIdx} className="matrix_row matrix_body_row">
                            {/* STICKY COLUMN 1: STUDENT */}
                            <div className="matrix_cell cell_student col_sticky_1">
                              <div className="student_name_bold" title={s.nickname}>
                                {s.nickname}
                              </div>
                              <div className="student_email_sub" title={s.email}>
                                {s.email}
                              </div>
                            </div>

                            {/* STICKY COLUMN 2: SCORE */}
                            <div className="matrix_cell cell_score col_sticky_2">
                              <div className="score_primary">
                                {typeof s.score === "string" && s.score.includes("/") ? (
                                  <>
                                    <span className="score_num">{s.score.split("/")[0].trim()}</span>
                                    <span className="score_denom"> / {s.score.split("/")[1].trim()}</span>
                                  </>
                                ) : (
                                  s.score
                                )}
                              </div>
                              <div className={`percentage_sub ${pctColorClass}`}>
                                ({s.percentage})
                              </div>
                            </div>

                          {/* SCROLLABLE QUESTION CELLS */}
                          {matrixQuestions.map((mq, qIdx) => {
                            const resp =
                              s.responses?.find(
                                (r) =>
                                  String(r.questionId) === String(mq.id) ||
                                  String(r.questionId) === String(mq.key)
                              ) ||
                              s.responses?.[qIdx] ||
                              null;

                            const isCorrect = resp ? Boolean(resp.isCorrect) : false;
                            const isSkipped = resp ? Boolean(resp.isSkipped) : true;
                            const displayText = resp?.answerText || (isSkipped ? "Skipped" : "—");

                            return (
                              <div key={qIdx} className="matrix_cell cell_question">
                                <div className="response_content">
                                  <div className="response_icon_status">
                                    {isSkipped ? (
                                      <span className="status_icon skipped_icon" title="Skipped">
                                        −
                                      </span>
                                    ) : isCorrect ? (
                                      <span className="status_icon check_icon" title="Correct">
                                        ✓
                                      </span>
                                    ) : (
                                      <span className="status_icon cross_icon" title="Incorrect">
                                        ✕
                                      </span>
                                    )}
                                  </div>
                                  <div className="response_text_block">
                                    <p className="chosen_answer_text" title={displayText}>
                                      <LatexRenderer text={displayText} inline />
                                    </p>
                                    <span
                                      className={`status_indicator ${
                                        isSkipped
                                          ? "text_gray"
                                          : isCorrect
                                          ? "text_green"
                                          : "text_red"
                                      }`}
                                    >
                                      {isSkipped
                                        ? "(Skipped)"
                                        : isCorrect
                                        ? "(Correct)"
                                        : "(Incorrect)"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })
                  ) : (
                    <div className="matrix_row empty_row">
                      <div className="matrix_cell empty_cell">
                        No response data available
                      </div>
                    </div>
                  )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* IMAGE ZOOM LIGHTBOX MODAL */}
      {zoomModalImage && (
        <div
          className="qb_image_zoom_overlay"
          onClick={() => {
            setZoomModalImage(null);
            setZoomScale(1.5);
          }}
        >
          <div
            className="qb_image_zoom_modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="zoom_modal_header">
              <span className="zoom_modal_title">
                {zoomModalImage.title || "Image Preview"}
              </span>
              <div className="zoom_controls_group">
                <button
                  type="button"
                  className="zoom_ctrl_btn"
                  title="Zoom Out"
                  onClick={() =>
                    setZoomScale((prev) =>
                      Math.max(0.5, Number((prev - 0.25).toFixed(2)))
                    )
                  }
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    <line x1="8" y1="11" x2="14" y2="11"></line>
                  </svg>
                </button>
                <span className="zoom_scale_indicator">
                  {Math.round(zoomScale * 100)}%
                </span>
                <button
                  type="button"
                  className="zoom_ctrl_btn"
                  title="Zoom In"
                  onClick={() =>
                    setZoomScale((prev) =>
                      Math.min(3.5, Number((prev + 0.25).toFixed(2)))
                    )
                  }
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    <line x1="11" y1="8" x2="11" y2="14"></line>
                    <line x1="8" y1="11" x2="14" y2="11"></line>
                  </svg>
                </button>
                <button
                  type="button"
                  className="zoom_close_btn"
                  title="Close Preview"
                  onClick={() => {
                    setZoomModalImage(null);
                    setZoomScale(1.5);
                  }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            </div>
            <div className="zoom_modal_body">
              <div className="zoom_img_scroll_container">
                <img
                  src={zoomModalImage.src}
                  alt="Zoom preview"
                  className="zoom_preview_img"
                  style={{
                    transform: `scale(${zoomScale})`,
                    transformOrigin: "top center",
                    transition: "transform 0.15s ease-out",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassroomQuizSummary;

